import { Alert, Anchor, Button, Group, PasswordInput, Stack, Text, UnstyledButton } from '@mantine/core'
import { IconBackspace } from '@tabler/icons-react'
import { hashKey, type QueryClient, useQueryClient } from '@tanstack/react-query'
import { pinIdleMinutes, pinLength, pinTries, weakPin } from '@tnn/core'
import { InitialsAvatar } from '@tnn/ui'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { api, isApiError, isTransient, lastRequestAt } from '../api/client'
import { AuthCard } from '../app/AuthCard'
import { loginUrl } from '../app/links'
import { meQueryKey } from './client'
import classes from './Pin.module.css'
import type { Me } from './session'

/**
 * The PIN (`@tnn/core` pin.ts): six digits that open the apps on a device signed in already, asked when they open or
 * come back after `pinIdleMinutes` away. The API holds the lock (`/api/me` says `pin.locked`; everything else answers
 * 403 PIN_LOCKED); here is the screen for it, set over apps already open so nothing in them is lost.
 */

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9']

/**
 * Six dots and a number pad, like a bank's app: a keyboard's digits work too. `submit` gets the six digits → what to
 * say when they are not right (the dots shake), or nothing
 */
function PinPad({
  label,
  notice,
  submit,
}: {
  label?: ReactNode
  /** Said under the dots from the start (the two PINs differed) */
  notice?: ReactNode
  submit: (pin: string) => Promise<ReactNode | null> | ReactNode | null
}) {
  const { t } = useTranslation()
  const [value, setValue] = useState('')
  const [error, setError] = useState<ReactNode>(notice ?? null)
  const [shakes, setShakes] = useState(notice ? 1 : 0)
  const [busy, setBusy] = useState(false)
  // What is typed so far, read at once: keys pressed quickly come before the next render
  const typed = useRef({ value: '', busy: false })
  const show = (next: string) => {
    typed.current.value = next
    setValue(next)
  }

  const press = async (digit: string) => {
    const now = typed.current
    if (now.busy || now.value.length >= pinLength) return
    const next = now.value + digit
    show(next)
    if (next.length < pinLength) return
    now.busy = true
    setBusy(true)
    const failed = await submit(next)
    now.busy = false
    setBusy(false)
    show('')
    setError(failed ?? null)
    if (failed) setShakes((n) => n + 1)
  }
  const erase = () => {
    if (!typed.current.busy) show(typed.current.value.slice(0, -1))
  }

  // Typed on a keyboard: digits and backspace, kept from whatever is under the lock
  const keys = useRef({ press, erase })
  keys.current = { press, erase }
  useEffect(() => {
    const pressed = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.metaKey || event.altKey) return
      if (/^\d$/.test(event.key)) void keys.current.press(event.key)
      else if (event.key === 'Backspace') keys.current.erase()
      else return
      event.preventDefault()
      event.stopPropagation()
    }
    window.addEventListener('keydown', pressed, { capture: true })
    return () => window.removeEventListener('keydown', pressed, { capture: true })
  }, [])

  return (
    <fieldset className={classes.pad} aria-label={t('pin.label')}>
      {label && (
        <Text size="md" fw={500} ta="center">
          {label}
        </Text>
      )}
      <div key={shakes} className={classes.dots} data-error={error ? '' : undefined} aria-hidden>
        {Array.from({ length: pinLength }, (_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: the dots are positions, nothing else
          <span key={i} className={classes.dot} data-filled={i < value.length || undefined} />
        ))}
      </div>
      <Text size="sm" c="var(--tnn-error)" className={classes.message} role="alert">
        {error}
      </Text>
      <div className={classes.keys}>
        {KEYS.map((digit) => (
          <UnstyledButton key={digit} className={classes.key} disabled={busy} onClick={() => void press(digit)}>
            {digit}
          </UnstyledButton>
        ))}
        <span />
        <UnstyledButton className={classes.key} disabled={busy} onClick={() => void press('0')}>
          0
        </UnstyledButton>
        <UnstyledButton className={`${classes.key} ${classes.erase}`} disabled={busy} onClick={erase} aria-label={t('pin.erase')}>
          <IconBackspace size={26} stroke={1.5} />
        </UnstyledButton>
      </div>
    </fieldset>
  )
}

/**
 * Setting the PIN: the password first when it is asked (`withPassword`; the API asks for it too when a sign-in did not
 * just open this session), then the PIN twice → saved, and this session opened with it → `onDone`
 */
export function PinSetup({ username, withPassword, onDone }: { username: string; withPassword: boolean; onDone: () => unknown }) {
  const { t } = useTranslation()
  const [needsPassword, setNeedsPassword] = useState(withPassword)
  const [step, setStep] = useState<'password' | 'enter' | 'confirm'>(withPassword ? 'password' : 'enter')
  const [password, setPassword] = useState('')
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [first, setFirst] = useState('')
  const [notice, setNotice] = useState<string | null>(null)

  const save = async (pin: string) => {
    try {
      await api.put('/me/pin', { pin, password: needsPassword ? password : undefined })
      await onDone()
      return null
    } catch (error) {
      // The password wrong, missing, or the name locked by wrong ones: back to it, saying so
      const aboutPassword = isApiError(error, 'VALIDATION') && error.issues?.some((issue) => issue.path[0] === 'password')
      if (aboutPassword || isApiError(error, 'RATE_LIMITED')) {
        setNeedsPassword(true)
        setPasswordError(isApiError(error, 'RATE_LIMITED') ? error.message : needsPassword ? t('pin.wrongPassword') : null)
        setStep('password')
        return null
      }
      return t(isTransient(error) ? 'errors.network' : 'errors.generic')
    }
  }

  if (step === 'password') {
    return (
      <form
        onSubmit={(event) => {
          event.preventDefault()
          if (!password) return
          setPasswordError(null)
          setStep('enter')
        }}
      >
        <Stack gap="md">
          <input type="text" name="username" autoComplete="username" value={username} readOnly hidden />
          <PasswordInput
            size="md"
            label={t('pin.password')}
            autoComplete="current-password"
            value={password}
            error={passwordError}
            onChange={(event) => setPassword(event.currentTarget.value)}
            data-autofocus
            autoFocus
          />
          <Button type="submit" size="md" disabled={!password} fullWidth>
            {t('pin.next')}
          </Button>
        </Stack>
      </form>
    )
  }
  if (step === 'enter') {
    return (
      <PinPad
        key="enter"
        label={t('pin.enter')}
        notice={notice}
        submit={(pin) => {
          if (weakPin(pin)) return t('pin.weak')
          setFirst(pin)
          setNotice(null)
          setStep('confirm')
          return null
        }}
      />
    )
  }
  return (
    <PinPad
      key="confirm"
      label={t('pin.confirm')}
      submit={(pin) => {
        if (pin === first) return save(pin)
        setNotice(t('pin.mismatch'))
        setStep('enter')
        return null
      }}
    />
  )
}

/** The lock lifted: `/api/me` asked again (it says so), and everything that was refused while it was locked */
async function reopen(client: QueryClient) {
  const me = hashKey(meQueryKey)
  void client.invalidateQueries({ predicate: (query) => query.queryHash !== me })
  await client.invalidateQueries({ queryKey: meQueryKey, exact: true })
}

/**
 * What a signed-in session shows until its PIN is typed: setting one first (with the password, unless a sign-in just
 * opened the session), else the PIN; forgot it → the password and a new one; `pinTries` wrong → signed out, to the
 * password
 */
export function PinScreen({ me, onSignOut }: { me: Me; onSignOut: () => Promise<void> }) {
  const { t } = useTranslation()
  const client = useQueryClient()
  const [mode, setMode] = useState<'pin' | 'reset' | 'out'>('pin')
  const opened = () => reopen(client)
  const signOut = (
    <Anchor component="button" type="button" size="sm" c="dimmed" onClick={() => void onSignOut()}>
      {t('pin.notYou', { name: me.user.name })} {t('auth.signOut')}
    </Anchor>
  )

  if (!me.pin?.set) {
    return (
      <AuthCard title={t('pin.setTitle')} subtitle={t('pin.setSubtitle', { minutes: pinIdleMinutes })}>
        <Stack gap="xl">
          <PinSetup username={me.user.username} withPassword={!!me.pin?.locked} onDone={opened} />
          <Group justify="center">{signOut}</Group>
        </Stack>
      </AuthCard>
    )
  }
  if (mode === 'out') {
    return (
      <AuthCard title={t('pin.unlockTitle')}>
        <Stack gap="lg">
          <Alert color="danger" variant="light" radius="md">
            {t('pin.signedOut', { count: pinTries })}
          </Alert>
          <Button
            size="md"
            fullWidth
            onClick={() => {
              client.clear()
              window.location.assign(loginUrl(window.location.pathname + window.location.search))
            }}
          >
            {t('pin.signInAgain')}
          </Button>
        </Stack>
      </AuthCard>
    )
  }
  if (mode === 'reset') {
    return (
      <AuthCard title={t('pin.resetTitle')} subtitle={t('pin.resetSubtitle')}>
        <Stack gap="xl">
          <PinSetup username={me.user.username} withPassword onDone={opened} />
          <Group justify="space-between">
            <Anchor component="button" type="button" size="sm" onClick={() => setMode('pin')}>
              {t('pin.unlockTitle')}
            </Anchor>
            {signOut}
          </Group>
        </Stack>
      </AuthCard>
    )
  }
  return (
    <AuthCard title={t('pin.unlockTitle')} subtitle={t('pin.unlockSubtitle')}>
      <div className={classes.who}>
        {/* Initials: their photo is a file, and files wait for the PIN too */}
        <InitialsAvatar name={me.user.name} size={40} />
        <Text fw={500} truncate>
          {me.user.name}
        </Text>
      </div>
      <Stack gap="xl">
        <PinPad
          submit={async (pin) => {
            try {
              const { unlocked, left } = await api.post<{ unlocked: boolean; left: number }>('/me/pin/unlock', { pin })
              if (unlocked) await opened()
              else if (!left) setMode('out')
              else return t('pin.wrong', { count: left })
              return null
            } catch (error) {
              return t(isTransient(error) ? 'errors.network' : 'errors.generic')
            }
          }}
        />
        <Group justify="space-between">
          <Anchor component="button" type="button" size="sm" onClick={() => setMode('reset')}>
            {t('pin.forgot')}
          </Anchor>
          {signOut}
        </Group>
      </Stack>
    </AuthCard>
  )
}

/** Over apps already open: they stay as they were underneath, out of sight and out of reach */
export function PinOver({ children }: { children: ReactNode }) {
  return <div className={classes.over}>{children}</div>
}

/**
 * Keeps the lock in step with the API while the apps are open: back after `pinIdleMinutes` out of sight, they lock at
 * once (before `/api/me` says whether another tab kept them open); someone at work on a page that asks the API nothing
 * for a minute (a long form) keeps them open with a light `/api/me`
 */
export function usePinWatch() {
  const client = useQueryClient()
  const hiddenAt = useRef(0)
  useEffect(() => {
    const away = pinIdleMinutes * 60_000
    const seen = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt.current = Date.now()
        return
      }
      if (!hiddenAt.current || Date.now() - hiddenAt.current < away) return
      client.setQueryData<Me>(meQueryKey, (me) => (me?.pin ? { ...me, pin: { ...me.pin, locked: true } } : me))
      void client.invalidateQueries({ queryKey: meQueryKey, exact: true })
    }
    const used = () => {
      if (document.visibilityState !== 'visible' || Date.now() - lastRequestAt() < 60_000) return
      void client.invalidateQueries({ queryKey: meQueryKey, exact: true })
    }
    const uses = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const
    document.addEventListener('visibilitychange', seen)
    for (const use of uses) window.addEventListener(use, used, { capture: true, passive: true })
    return () => {
      document.removeEventListener('visibilitychange', seen)
      for (const use of uses) window.removeEventListener(use, used, { capture: true })
    }
  }, [client])
}
